import React, { memo } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { DOC_TYPES, GUARANTOR_RELATIONS } from "../../constants/registrationConstants";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { useSectionConfig } from "../../context/SectionConfigContext";

const S = CONFIGURED_SECTION_IDS;

function Guarantors({ patient, updateListItem, addListItem, removeListItem, errors = {} }) {
  const { canAddListEntry, canRemoveListEntry, config, getListFieldGridSize } = useSectionConfig();
  const md = config ? getListFieldGridSize(S.guarantors) : 2;
  const defaultItem = { name: "", relation: "", phone: "", idType: "", idValue: "" };

  return (
    <Box>
      {errors.guarantors__min && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {errors.guarantors__min}
        </Typography>
      )}
      {patient.guarantors.map((g, i) => (
        <Grid container spacing={2} key={i} alignItems="flex-start" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
          <ConfigurableField sectionId={S.guarantors} fieldName="name" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Name" required={required} error={errors[`guarantors.${i}.name`]}>
                <IpdTextField fullWidth size="small" value={g.name} onChange={(e) => updateListItem("guarantors", i, "name", e.target.value)} error={Boolean(errors[`guarantors.${i}.name`])} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.guarantors} fieldName="relation" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Relation" required={required} error={errors[`guarantors.${i}.relation`]}>
                <IpdTextField select fullWidth size="small" value={g.relation} onChange={(e) => updateListItem("guarantors", i, "relation", e.target.value)} error={Boolean(errors[`guarantors.${i}.relation`])}>
                  <MenuItem value="">Select</MenuItem>
                  {GUARANTOR_RELATIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.guarantors} fieldName="phone" xs={12} md={md}>
            {({ required }) => (
              <FormField label="Phone" required={required} error={errors[`guarantors.${i}.phone`]}>
                <IpdTextField fullWidth size="small" value={g.phone} onChange={(e) => updateListItem("guarantors", i, "phone", e.target.value)} error={Boolean(errors[`guarantors.${i}.phone`])} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.guarantors} fieldName="idType" xs={12} md={md}>
            {({ required }) => (
              <FormField label="ID Type" required={required} error={errors[`guarantors.${i}.idType`]}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  size="small"
                  options={DOC_TYPES}
                  value={g.idType || ""}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt.replace(/_/g, " ") : "")}
                  onInputChange={(_e, value, reason) => {
                    if (reason === "reset") return;
                    updateListItem("guarantors", i, "idType", value ?? "");
                  }}
                  onChange={(_e, value) => updateListItem("guarantors", i, "idType", value ?? "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(errors[`guarantors.${i}.idType`])}
                      placeholder="Select or type ID type"
                    />
                  )}
                />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.guarantors} fieldName="idValue" xs={12} md={md}>
            {({ required }) => (
              <FormField label="ID Value" required={required} error={errors[`guarantors.${i}.idValue`]}>
                <IpdTextField fullWidth size="small" value={g.idValue} onChange={(e) => updateListItem("guarantors", i, "idValue", e.target.value)} error={Boolean(errors[`guarantors.${i}.idValue`])} />
              </FormField>
            )}
          </ConfigurableField>
          {canRemoveListEntry(S.guarantors, patient.guarantors.length) && (
            <Grid item xs={12} md={1}>
              <IconButton aria-label="Remove guarantor" onClick={() => removeListItem("guarantors", i)}><DeleteIcon /></IconButton>
            </Grid>
          )}
        </Grid>
      ))}
      {canAddListEntry(S.guarantors, patient.guarantors.length) && (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addListItem("guarantors", { ...defaultItem })}>
          Add Guarantor
        </Button>
      )}
    </Box>
  );
}

export default memo(Guarantors);
